import { Button } from '@hologram-dimension/button';
import { Modal } from '@hologram-dimension/modal';
import clsx from 'clsx/lite';
import TagManagerComponent from 'components/TagManager/TagManager';
import { useCallback, useRef, useState, type FC } from 'react';
import { StructsTagResponseWithDetails } from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';
import formatNumber from 'utils/numbers/formatNumber';
import type { CommonModalProps } from '../shared';
import styles from './TagManager.module.css';

interface TagManagerModalProps extends CommonModalProps {
  sims: HologramSimIdsMap;
  onSuccess?: (sims: Array<HologramSimId>) => void;
  onError?: (error?: string) => void;
  /**
   * Optional, handle committed (synced) changes to tags on modal close. Use onSuccess for immediate changes
   * @param lastTags The tags updated to be used to create `useTags*` mutates
   */
  onDone?: (lastTags: Array<StructsTagResponseWithDetails>) => void;
}

const TagManagerModal: FC<TagManagerModalProps> = ({
  onClose,
  defaultOpen = false,
  className,
  sims,
  onSuccess: onSuccessParent,
  onError,
  onDone,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [processing, setIsProcessing] = useState(false);
  const submitRef = useRef<OnSubmit>();
  const [lastTags, setLastTags] = useState<Array<StructsTagResponseWithDetails>>([]);

  const onProcessing = useCallback((value: boolean) => {
    setIsProcessing(value);
  }, []);

  const handleClose = useCallback(() => {
    if (onDone) {
      onDone(lastTags);
    }
    onClose();
  }, [lastTags, onClose, onDone]);

  const onSuccess = useCallback(
    (modifiedSims?: Array<HologramSimId>) => {
      // If we send success for single sim mode, we send too many requests and parents constantly refresh
      if (sims.size > 1) {
        if (modifiedSims) {
          if (onSuccessParent) {
            onSuccessParent(modifiedSims);
          }
        }
        handleClose();
      }
    },
    [handleClose, onSuccessParent, sims.size]
  );

  const handleSubmit: React.MouseEventHandler &
    React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };

  return (
    <Modal
      className={clsx(className, styles.wrapper)}
      header={
        !sims.size || sims.size === 1
          ? 'Manage tags'
          : `Manage tags for ${formatNumber(sims.size)} SIMs`
      }
      onClosed={handleClose}
      open={open}
      setOpen={setOpen}
      noContentPadding
      footerActions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {sims.size === 1 ? 'Close' : 'Cancel'}
          </Button>
          {sims.size > 1 && (
            <Button loading={processing} onClick={handleSubmit} variant="primary">
              {`Change tags for ${formatNumber(sims.size)} SIMs`}
            </Button>
          )}
        </>
      }
    >
      <TagManagerComponent
        selectedSimIds={sims}
        onProcessing={onProcessing}
        ref={submitRef}
        onSuccess={onSuccess}
        onError={onError}
        onTagsMutated={setLastTags}
        variant="modal"
        simInventoryBaseUrl="Org :orgid"
      />
    </Modal>
  );
};

export default TagManagerModal;
